// report-ui.js — gedeelde stukjes voor de rapportageschermen (lijst, nieuw rapport, editor).
//
// Elke waarde in een rapport heeft een BRON (project, veldwerkopdracht, boringen, …) en elk blok
// een SOORT (uit de data, vaste tekst, voorstel Flip AI). Beide krijgen overal dezelfde kleur, zodat
// je na één keer kijken weet wat je ziet. De kleuren staan hier op één plek, met een donkere
// variant: losse hex-waarden in een component zie je in het donkere thema meteen terug als vlek.

import { css } from 'lit';

/** Bronlabels: tekst en kleurklasse. */
export const SOURCE_TAGS = {
  standard: { label: 'Standaard', cls: 't-grey' },
  project: { label: 'Project', cls: 't-green' },
  fieldOrder: { label: 'Veldwerkopdracht', cls: 't-purple' },
  borings: { label: 'Boringen', cls: 't-blue' },
  sublocations: { label: 'Monsterplan', cls: 't-orange' },
  lab: { label: 'Labopdrachten', cls: 't-teal' },
  report: { label: 'Rapport', cls: 't-grey' },
  manual: { label: 'Handmatig', cls: 't-amber' },
  template: { label: 'Sjabloon', cls: 't-grey' },
  map: { label: 'Kaart', cls: 't-orange' },
};

/**
 * De bron van een veld. Nieuwe blokken hebben een `tag`; oudere hebben alleen `source`
 * (tabel.kolom), daar leiden we hem uit af.
 */
export function fieldTag(f) {
  if (f?.tag && SOURCE_TAGS[f.tag]) return f.tag;
  const s = String(f?.source || '');
  if (s.startsWith('projects.')) return 'project';
  if (s.startsWith('field_orders')) return 'fieldOrder';
  if (s.startsWith('borings')) return 'borings';
  if (s.includes('sublocations')) return 'sublocations';
  if (s.startsWith('handmatig')) return 'manual';
  return 'report';
}

/** Soort blok: kleur van het bolletje en het label boven het blok. */
export const BLOCK_KINDS = {
  data: { label: 'Uit de data', cls: 'k-data' },
  fixed: { label: 'Vaste tekst', cls: 'k-fixed' },
  variant: { label: 'Keuzetekst', cls: 'k-fixed' },
  ai: { label: 'Voorstel Flip AI', cls: 'k-ai' },
  manual: { label: 'Zelf geschreven', cls: 'k-manual' },
};

export const reportUiStyles = css`
  .tag { display:inline-block; font-size:.6875rem; font-weight:700; padding:2px 7px; border-radius:var(--mv-radius-xs, 5px); white-space:nowrap; }
  .t-grey { background:var(--mv-surface-alt); color:var(--mv-text-sub); }
  .t-green { background:var(--mv-primary-tint); color:#2F6B22; }
  .t-purple { background:#F3EEFD; color:#5B2BC4; }
  .t-blue { background:#E8EEFD; color:#1D4ED8; }
  .t-orange { background:#FBEFE6; color:#C2410C; }
  .t-teal { background:#E6F4F7; color:#0E6E80; }
  .t-amber { background:#FFF8EC; color:#8A5B10; }
  :host-context([data-theme='dark']) .t-green { color:#9BD38A; }
  :host-context([data-theme='dark']) .t-purple { background:#2B2142; color:#C4AEF5; }
  :host-context([data-theme='dark']) .t-blue { background:#1C2742; color:#A5BCF5; }
  :host-context([data-theme='dark']) .t-orange { background:#3A2618; color:#F2A878; }
  :host-context([data-theme='dark']) .t-teal { background:#14323A; color:#86D0DD; }
  :host-context([data-theme='dark']) .t-amber { background:#33280F; color:#E3B341; }

  .kind { display:inline-flex; align-items:center; gap:6px; font-size:.72rem; font-weight:600; }
  .kind::before { content:''; width:6px; height:6px; border-radius:50%; background:currentColor; }
  .k-data { color:#2F6B22; }
  .k-fixed { color:var(--mv-text-sub); }
  .k-ai { color:#5B2BC4; }
  .k-manual { color:#8A5B10; }
  :host-context([data-theme='dark']) .k-data { color:#9BD38A; }
  :host-context([data-theme='dark']) .k-ai { color:#C4AEF5; }
  :host-context([data-theme='dark']) .k-manual { color:#E3B341; }

  .caps { font-size:.6875rem; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:var(--mv-text-muted); }
  .btn { display:inline-flex; align-items:center; gap:6px; border-radius:var(--mv-radius, 9px); padding:8px 14px; font:inherit; font-size:.8125rem;
         font-weight:600; cursor:pointer; white-space:nowrap; border:1px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text); }
  .btn:hover:not(:disabled) { border-color:var(--mv-primary); }
  .btn:disabled { opacity:.55; cursor:default; }
  .btn.primary { background:var(--mv-primary); border-color:var(--mv-primary); color:#fff; }
  .btn.primary:hover:not(:disabled) { background:var(--mv-primary-hover); }
  .btn.ghost { color:var(--mv-text-sub); }
  .check { width:18px; height:18px; flex:none; border-radius:5px; display:inline-flex; align-items:center; justify-content:center;
           font-size:11px; font-weight:700; color:#fff; border:1.5px solid var(--mv-border); background:var(--mv-surface); }
  .check.on { background:var(--mv-primary); border-color:var(--mv-primary); }
  .status-pill { font-size:.65rem; font-weight:700; letter-spacing:.07em; text-transform:uppercase; padding:3px 8px; border-radius:var(--mv-radius-full, 999px);
                 background:var(--mv-warning-panel-bg); color:var(--mv-warning-text); }
  .status-pill.approved { background:var(--mv-primary-tint); color:var(--mv-primary); }
  .status-pill.review { background:#E8EEFD; color:#1D4ED8; }
  .status-pill.superseded { background:var(--mv-surface-alt); color:var(--mv-text-muted); }
`;

/** Statussen van een rapport (featureplan §8). */
export const REPORT_STATUS = {
  draft: { label: 'Concept', cls: '' },
  in_review: { label: 'Ter review', cls: 'review' },
  approved: { label: 'Goedgekeurd', cls: 'approved' },
  superseded: { label: 'Vervangen', cls: 'superseded' },
};

/** Hoeveel velden van een veldwerkopdracht zijn ingevuld (lijst = één veld). */
export function countFilledFields(v) {
  if (v == null) return 0;
  if (Array.isArray(v)) return v.length ? 1 : 0;
  if (typeof v === 'object') return Object.values(v).reduce((n, x) => n + countFilledFields(x), 0);
  if (typeof v === 'string') return v.trim() ? 1 : 0;
  return 1;
}
